import { useState } from "react"
import styled from "styled-components"

import {
  Button,
  colors,
  spacing,
  Text,
  TextArea,
} from "@minuthq/meatball-ui-react"

import { MessageProps } from "src/components/GuestInbox/GuestChat/Message"
import { useFetchHomeGuests } from "src/data/guests/queries/guestQueries"
import { TGuest } from "src/data/guests/types/guestTypes"
import { useFetchParadiseHomes } from "src/data/paradise/paradiseHomes/queries/paradiseHomeQueries"
import { IParadiseHome } from "src/data/paradise/paradiseHomes/types/paradiseHomeQuerytypes"
import { usePostParadiseLLMPromptTestMessage } from "src/data/paradise/paradiseLLMPrompts/queries/paradiseLLMPromptsQueries"
import { TParadiseLLMPrompt } from "src/data/paradise/paradiseLLMPrompts/types/paradiseLLMPromptsQueryTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { ChatPanel } from "src/ui/ChatPanel/ChatPanel"
import { DropdownSelect } from "src/ui/DropdownSelect/DropdownSelect"
import { MCard } from "src/ui/MCard/MCard"

export function ParadiseLLMPromptChat({
  prompt,
}: {
  prompt: TParadiseLLMPrompt
}) {
  const user = useGetUser()

  const [messages, setMessages] = useState<MessageProps[]>([])

  const [input, setInput] = useState("")
  const [selectedHome, setSelectedHome] = useState<IParadiseHome | null>(null)
  const [selectedGuest, setSelectedGuest] = useState<TGuest | null>(null)

  const [searchedHomeId, setSearchedHomeId] = useState("")

  const fetchHomes = useFetchParadiseHomes({
    filter: {
      home_id: searchedHomeId,
    },
    options: {
      keepPreviousData: true,
    },
  })

  const fetchHomesGuests = useFetchHomeGuests({
    homeId: selectedHome?.id ?? "",
    options: {
      enabled: !!selectedHome,
    },
  })

  const postParadiseLLMPromptTestMessage = usePostParadiseLLMPromptTestMessage()

  function sendTestMessage() {
    if (!input) {
      return
    }

    setMessages((prev) => [
      ...prev,
      {
        id: (prev.length + 1).toString(),
        content: input,
        senderType: "self",
        sentAt: new Date().toISOString(),
        source: "paradise",
        senderName: user.fullname,
      },
    ])

    postParadiseLLMPromptTestMessage.mutate(
      {
        path: {
          prompt_id: prompt.id,
        },
        body: {
          input,
          home_id: selectedHome?.id,
          guest_id: selectedGuest?.guest_id,
          messages: messages.map((message) => ({
            role: message.senderType === "self" ? "user" : "assistant",
            content: message.content,
          })),
        },
      },
      {
        onSuccess(data) {
          setMessages((prev) => [
            ...prev,
            {
              id: (prev.length + 1).toString(),
              content: data.response,
              senderType: "other",
              sentAt: new Date().toISOString(),
              source: "paradise",
              senderName: "LLM",
            },
          ])
        },
      }
    )

    setInput("")
  }

  return (
    <StyledCard boxShadow={false} border padding={0}>
      <Header>
        <Text variant="heading3">Preview prompt</Text>
      </Header>
      <ChatPanel
        messages={messages}
        onLoadMore={() => {}}
        isLoadingMore={false}
        renderInput={() => (
          <InputWrapper>
            <TextArea
              placeholder="Type your message here..."
              value={input}
              onChange={(value) => setInput(value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  sendTestMessage()
                }
              }}
              minRows={3}
            />
            <BottomWrapper>
              <ConfigWrapper>
                <DropdownSelect
                  label={selectedHome?.name ?? "Select home"}
                  searchText={searchedHomeId}
                  onSearch={setSearchedHomeId}
                  options={
                    fetchHomes.data?.homes.map((home) => ({
                      label: home.name,
                      value: home.id,
                      description: home.id,
                      home,
                    })) ?? []
                  }
                  selectedValue={selectedHome?.id ?? null}
                  onChange={({ option }) => setSelectedHome(option.home)}
                  showCheckmark={false}
                />
                <DropdownSelect
                  label={selectedGuest?.name ?? "Select guest"}
                  options={
                    fetchHomesGuests.data?.guests.map((guest) => ({
                      label: guest.name,
                      value: guest.guest_id,
                      description: guest.guest_id,
                      guest,
                    })) ?? []
                  }
                  selectedValue={selectedGuest?.guest_id ?? null}
                  onChange={({ option }) => setSelectedGuest(option.guest)}
                  showCheckmark={false}
                  disabled={!selectedHome}
                />
              </ConfigWrapper>

              <Button
                onClick={sendTestMessage}
                color="neutral"
                size="small"
                loading={postParadiseLLMPromptTestMessage.isPending}
              >
                Send
              </Button>
            </BottomWrapper>
          </InputWrapper>
        )}
      />
    </StyledCard>
  )
}

const StyledCard = styled(MCard)`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.M};
`

const InputWrapper = styled.div`
  display: grid;
  gap: ${spacing.S};
  border-top: 1px solid ${colors.divider};
  padding: ${spacing.M};
`

const BottomWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.S};
  flex-wrap: wrap;
`

const ConfigWrapper = styled.div`
  display: flex;
  gap: ${spacing.S};
  flex-wrap: wrap;
`
